import './package-picker.scss';

import { Col, Empty, Row } from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import React, { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Hint from '../../components/hint/hint';
import GamePackage from '../../components/game-package/game-package';
import PlayerCountUpsell from '../../components/player-count-upsell/player-count-upsell';
import useUpsellAccept from '../../components/player-count-upsell/useUpsellAccept';
import TieredGroupSizeDiscount from '../../components/tiered-group-size-discount/tiered-group-size-discount';
import { DISCOUNT_TIERS as TIERED_GROUP_SIZE_DISCOUNT_TIERS, CAMPAIGN_CONFIG } from '../../components/tiered-group-size-discount/tieredGroupSizeDiscountConfig';
import useTieredGroupSizeDiscountAccept from '../../components/tiered-group-size-discount/useTieredGroupSizeDiscountAccept';

import Session from '../../interfaces/ISession';
import { IAnalyticsAction, IApplicationState } from '../../store';
import { GoogleTagManagerEvents } from '../../helpers/googleTagManager';
import { actionCreators as SessionActions } from '../../store/Sessions';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import { actionCreators as PlayerActions } from '../../store/Players';
import { actionCreators as RewardActions } from '../../store/Rewards';
import { isFeatureEnabled, KnownFlag } from '../../store/featureFlags';
import { PurchaseTypes } from '../../store/PurchaseTypes';
import { PlayerCountUpsellFlow } from '../../helpers/PlayerCountUpsellFlow';

const PackagePicker: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const selectedTime = useSelector((state: IApplicationState) => state.session.selectedTime);
    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    const booking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
    const selectedSession = useSelector((state: IApplicationState) => state.session.selectedSession);
    const sessions = useSelector((state: IApplicationState) => state.session.sessions);
    const site = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const selectedPlayers = useSelector((state: IApplicationState) => state.players.selectedPlayers);
    const selectedPrivateUpgrade = useSelector((state: IApplicationState) => state.players.selectedPrivateUpgrade);
    const priceSets = useSelector((state: IApplicationState) => state.booking.priceSets);

    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const bookingOptions = useSelector((state: IApplicationState) => state.sites.bookingOptions);
    const rewardFlows = useSelector((state: IApplicationState) => state.rewards.flows);
    const upsellWasShown = useSelector((state: IApplicationState) => state.rewards.upsellWasShown);
    const bookingLoading = useSelector((state: IApplicationState) => state.booking.loading);
    const featureFlags = useSelector((state: IApplicationState) => state.featureFlags);
    const recentlySoldEnabled = isFeatureEnabled(featureFlags, KnownFlag.recentlySoldCounts);
    const upsellFlagEnabled = isFeatureEnabled(featureFlags, KnownFlag.playerCountUpsell);
    const tieredGroupSizeDiscountEnabled = CAMPAIGN_CONFIG.enabled;

    const priceSet = priceSets?.find((p) => p.venue === site?.urlKey);

    const emptyLogo = `/static/zl-black-new.png`;

    const filteredSessions = useMemo(() => {
        if (!selectedTime || !sessions) return [];
        var session = sessions.get(selectedTime.time);
        if (session && selectedPlayers) {
            for (var i = session.length - 1; i >= 0; i--) {
                if (
                    session[i] &&
                    (session[i].remainingSlots < selectedPlayers ||
                        (session[i].openTimeMinimumPlayers > selectedPlayers && session[i].bookedSlots === 0) ||
                        session[i].isPrivate)
                ) {
                    session.splice(i, 1);
                }
            }
        }
        return session;
    }, [selectedTime, sessions, selectedPlayers]);

    const sessionSelected = useCallback(
        (session: Session) => {
            if (booking && bookingStatus === BookingStatus.Pending && booking.packageId !== session.packageId) {
                dispatch(BookingActions.expireBooking());
            }

            dispatch(SessionActions.postSessionSelectedAction(session));
        },
        [dispatch, booking, bookingStatus]
    );

    const privacySelected = useCallback(
        (isPrivate: boolean) => {
            if (booking && bookingStatus === BookingStatus.Pending && selectedPrivateUpgrade !== isPrivate) {
                dispatch(BookingActions.expireBooking());
            }

            dispatch(PlayerActions.selectPrivateUpgrade(isPrivate));
        },
        [dispatch, booking, bookingStatus, selectedPrivateUpgrade]
    );

    // --- PlayerCountUpsell ---
    const upsellFlow = useMemo(() => new PlayerCountUpsellFlow(rewardFlows), [rewardFlows]);
    const upsellEligible = useMemo(() => {
        return upsellFlow.isEligible(
            upsellFlagEnabled,
            selectedPlayers,
            selectedPurchaseType,
            selectedSession?.package?.attractionTypeId,
            selectedPrivateUpgrade,
            selectedSession?.remainingSlots,
            bookingOptions?.maximumPlayers ?? 0
        );
    }, [upsellFlow, upsellFlagEnabled, selectedPlayers, selectedPurchaseType, selectedSession, selectedPrivateUpgrade, bookingOptions]);

    const isOfferMet = selectedPlayers != null && upsellFlow.isOfferMet(selectedPlayers);
    const showUpsell = selectedSession && (upsellEligible || (upsellWasShown && !selectedPrivateUpgrade && upsellFlow.isPackageEligible(selectedSession.package?.attractionTypeId)));

    useEffect(() => {
        if (showUpsell && !upsellWasShown && selectedPlayers) {
            dispatch(RewardActions.upsellInitialCount(selectedPlayers));
            dispatch(RewardActions.upsellWasShown('inline', selectedPlayers, upsellFlow.minimumTicketCount));
        }
    }, [showUpsell, upsellWasShown, selectedPlayers, upsellFlow.minimumTicketCount, dispatch]);

    useEffect(() => {
        if (upsellWasShown && (!upsellFlagEnabled || selectedPrivateUpgrade)) {
            dispatch(RewardActions.upsellReset());
        }
    }, [upsellFlagEnabled, upsellWasShown, selectedPrivateUpgrade, dispatch]);

    const handleUpsellAccept = useUpsellAccept(upsellFlow);

    // --- TieredGroupSizeDiscount ---
    const isBookingReserved = booking && bookingStatus === BookingStatus.Pending;
    const showTieredGroupSizeDiscount = tieredGroupSizeDiscountEnabled
        && selectedPurchaseType === PurchaseTypes.Booking
        && selectedSession != null
        && (CAMPAIGN_CONFIG.attractionTypeId === undefined || selectedSession.package?.attractionTypeId === CAMPAIGN_CONFIG.attractionTypeId)
        && selectedPlayers != null
        && !isBookingReserved
        && Math.min(bookingOptions?.maximumPlayers ?? 0, selectedSession.remainingSlots ?? 0) >= TIERED_GROUP_SIZE_DISCOUNT_TIERS[0].threshold;

    const tieredGroupSizeDiscountShownRef = React.useRef(false);
    useEffect(() => {
        if (showTieredGroupSizeDiscount && selectedPlayers && !tieredGroupSizeDiscountShownRef.current) {
            tieredGroupSizeDiscountShownRef.current = true;
            dispatch({
                type: 'TIERED_GROUP_SIZE_DISCOUNT_SHOWN',
                googleTagManagerEvent: GoogleTagManagerEvents.TieredGroupSizeDiscountShown('inline', selectedPlayers),
            } as IAnalyticsAction);
        }
    }, [showTieredGroupSizeDiscount, selectedPlayers, dispatch]);

    const handleTieredGroupSizeDiscountAccept = useTieredGroupSizeDiscountAccept();

    const upsellTicketPrice = selectedSession?.package.packageTierPrices.find(
        (ptp) => ptp.priceTierType.priceTierTypeId === selectedSession.priceTierTypeId
    )?.price;

    if (filteredSessions === undefined || filteredSessions.length === 0)
        return (
            <React.Fragment>
                <Row justify="start" align="top">
                    <Col span={24}>
                        <Empty
                            description={t('packagePicker.labels.noSessions')}
                            image={emptyLogo}
                            imageStyle={{
                                opacity: 0.1,
                                height: 50,
                                margin: 20,
                            }}
                        />
                    </Col>
                </Row>
            </React.Fragment>
        );

    return (
        <React.Fragment>
            <Row gutter={8} justify="center">
                <Col span={24}>
                    {filteredSessions.map((session: Session) => {
                        return (
                            <React.Fragment key={`${session.sessionName}-${session.packageId}`}>
                                <GamePackage
                                    session={session}
                                    site={site}
                                    priceSet={priceSet}
                                    isSelected={session === selectedSession}
                                    locale={selectedLanguage.code}
                                    onClick={sessionSelected}
                                    selectedPlayers={selectedPlayers}
                                    selectedPrivateUpgrade={selectedPrivateUpgrade}
                                    onUpgradeCheckChanged={privacySelected}
                                    recentlySoldEnabled={recentlySoldEnabled}
                                />
                                {session === selectedSession && showUpsell && selectedPlayers && (
                                    <PlayerCountUpsell
                                        currentPlayers={selectedPlayers}
                                        minimumPlayers={upsellFlow.minimumTicketCount}
                                        maximumPlayers={Math.min(
                                            bookingOptions?.maximumPlayers ?? 0,
                                            selectedSession?.remainingSlots ?? 0
                                        )}
                                        locale={selectedLanguage.code}
                                        onAccept={handleUpsellAccept}
                                        variant="inline"
                                        accepted={isOfferMet}
                                        ticketPrice={upsellTicketPrice?.listPrice}
                                        currencyCode={upsellTicketPrice?.currencyCode}
                                        loading={bookingLoading}
                                        onExpand={() => dispatch(RewardActions.upsellCardExpanded())}
                                    />
                                )}
                                {session === selectedSession && showTieredGroupSizeDiscount && selectedPlayers && (
                                    <TieredGroupSizeDiscount
                                        currentPlayers={selectedPlayers}
                                        maximumPlayers={Math.min(
                                            bookingOptions?.maximumPlayers ?? 0,
                                            selectedSession?.remainingSlots ?? 0
                                        )}
                                        locale={selectedLanguage.code}
                                        onAccept={handleTieredGroupSizeDiscountAccept}
                                        variant="inline"
                                        loading={bookingLoading}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </Col>
            </Row>
            <Row justify="start" align="top">
                <Col span={24}>
                    <Hint>
                        <Text>{t('packagePicker.hint.additionalPackages')}</Text>
                    </Hint>
                    <br />
                    <Paragraph>
                        <Text className="text-small">{t('packagePicker.hint.approxTimes')}</Text>
                    </Paragraph>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default React.memo(PackagePicker);
